<template>
  <main class="main">
    <portal to="headerTitle">
      {{ $t('admin.pageHotelSettings.title') }}
    </portal>
    <template v-if="!isNil(hotel)">
      <card :title="$t('admin.pageHotelSettings.card.general')" cols="12 6@desktop" rows="3">
        <hm-card-inner :title="$t('admin.pageHotelSettings.card.checkInCheckOutTimes')">
          <hm-input-time-of-day-slider v-model="hotel.checkOutTimeFrom"
                                       :step="0.25"
                                       :label="$t('admin.pageHotel.checkOutTimeFrom')"
                                       name="checkOutTimeFrom" />
          <hm-input-time-of-day-slider v-model="hotel.checkOutTime"
                                       :step="0.25"
                                       :label="$t('admin.pageHotel.checkOutTime')"
                                       name="checkOutTime" />
          <hm-input-time-of-day-slider v-model="hotel.checkInTime"
                                       :step="0.25"
                                       :label="$t('admin.pageHotel.checkInTime')"
                                       name="checkInTime" />
          <hm-input-time-of-day-slider v-model="hotel.checkInTimeUntil"
                                       :step="0.25"
                                       :label="$t('admin.pageHotel.checkInTimeUntil')"
                                       name="checkInTimeUntil" />
          <template v-if="hasLicense('AutoCheckInCheckOut')">
            <hm-input-toggle v-model="hotel.autoCheckIn"
                             :label="$t('admin.pageHotel.autoCheckIn')"
                             :enabled-title="$t('admin.pageHotel.autoCheckInEnabled')"
                             :disabled-title="$t('admin.pageHotel.autoCheckInDisabled')"
                             name="autoCheckIn" />
            <hm-input-toggle v-model="hotel.autoCheckOut"
                             :label="$t('admin.pageHotel.autoCheckOut')"
                             :enabled-title="$t('admin.pageHotel.autoCheckOutEnabled')"
                             :disabled-title="$t('admin.pageHotel.autoCheckOutDisabled')"
                             name="autoCheckOut" />
            <hm-input-select v-model="hotel.autoCheckInCheckOutMaxDelay"
                             :label="$t('admin.pageHotel.autoCheckInCheckOutMaxDelay')"
                             :help="$t('admin.pageHotel.autoCheckInCheckOutMaxDelay_help')">
              <option v-for="i in [0, 30, 60, 120, 180, 240, 300, 450, 600]" :value="i" :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') && i < 180">{{ String(Math.floor(i / 60)).padStart(2, '0') + ':' + String(i % 60).padStart(2, '0') }}</option>
            </hm-input-select>
          </template>
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageHotelSettings.card.display')">
          <hm-input-select v-model="hotel.reportDisplayMode" :label="$t('admin.pageHotelSettings.reportDisplayMode')" enum-name="ReportDisplayMode" />
          <hm-input-file v-model="hotel.emailHeaderImage"
                         :url="serviceUrls.hotelDetails.uploadFile(this, 'emailHeaderImage')"
                         :label="$t('admin.pageHotelSettings.emailHeaderImage')"
                         :help="$t('admin.pageHotelSettings.emailHeaderImage_help')"
                         cols="12" />
          <hm-input-toggle v-model="hotel.hideLogoInEmails" :label="$t('admin.pageHotelSettings.hideLogoInEmails')" name="hideLogoInEmails" />
          <hm-input v-model="hotel.emailAccentColor" :label="$t('admin.pageHotelSettings.emailAccentColor')" name="emailAccentColor">
            <template slot="icon-inline-left">
              <div :class="['hm-input__input__color--custom']" :style="`--icon-background-color: ${hotel.emailAccentColor}`" class="hm-input__input__color">&nbsp;</div>
            </template>
          </hm-input>
        </hm-card-inner>
        <hm-card-inner :title="$t('admin.pageHotelSettings.card.paymentsRatesInvoices')">
          <hm-input-toggle v-model="hotel.additionalFields.SUMMARIZE_ROOM_CHARGE_TRANSACTIONS"
                           :label="$t('admin.pageHotelSettings.summarizeRoomChargeTransactions')"
                           :help="$t('admin.pageHotelSettings.summarizeRoomChargeTransactions_help')"
          />
          <hm-input-number v-model="hotel.wireTransferTermOfPayment"
                           :max-fraction-digits="0"
                           :min-fraction-digits="0"
                           :label="$t('admin.pageHotelSettings.wireTransferTermOfPayment')"
                           :help="$t('admin.pageHotelSettings.wireTransferTermOfPayment_help')" />
          <hm-input-number v-model="hotel.roundDigits"
                           :max-fraction-digits="0"
                           :min-fraction-digits="0"
                           :label="$t('admin.pageHotelSettings.roundDigits')"
                           :help="$t('admin.pageHotelSettings.roundDigits_help')" />
          <hm-input-toggle v-model="hotel.chargeAtLeastMinimumOccupancyAmount" :label="$t('admin.pageHotelSettings.chargeAtLeastMinimumOccupancyAmount')" :help="$t('admin.pageHotelSettings.chargeAtLeastMinimumOccupancyAmount_help')" />
        </hm-card-inner>
      </card>
      <card :title="$t('admin.pageHotelSettings.card.additional')"
            cols="12 6@desktop"
            rows="2">
        <hm-card-inner>
          <hm-input-number v-model="hotel.futureDates"
                           :max-fraction-digits="0"
                           :min-fraction-digits="0"
                           :label="$t('admin.pageHotelSettings.futureDates')"
                           :help="$t('admin.pageHotelSettings.futureDates_help')" />
          <hm-input-select v-model="hotel.defaultReservationStatus" :label="$t('admin.pageHotelSettings.defaultReservationStatus')">
            <option value="Guaranteed" v-html="`${$t('ReservationStatus.Guaranteed')}`"></option>
            <option value="NonGuaranteed" v-html="`${$t('ReservationStatus.NonGuaranteed')}`"></option>
          </hm-input-select>
          <hm-input-toggle v-model="hotel.autoAssignRoomNumber" :label="$t('admin.pageHotelSettings.autoAssignRoomNumber')" />
          <hm-input-toggle v-model="hotel.additionalFields.ROOMPLAN_CHANNEL_ICONS" :label="$t('admin.pageHotelSettings.roomplanChannelIcons')" />
          <hm-input-toggle v-model="hotel.additionalFields.ROOMPLAN_PAID_ICONS" :label="$t('admin.pageHotelSettings.roomplanPaidIcons')" />
          <!-- disabled because this is not part of the protobuf schema used for the roomplan service -->
          <hm-input-toggle v-model="hotel.additionalFields.ROOMPLAN_RESERVATION_ICONS" :label="$t('admin.pageHotelSettings.roomplanReservationIcons')" />
          <hm-input-toggle v-model="hotel.additionalFields.LEGACY_POLICY_DESCRIPTIONS" :label="$t('admin.pageHotelSettings.legacyPolicyDescriptions')" />
          <hm-input-toggle v-model="hotel.roomPlanLastNameFirst" :label="$t('admin.pageHotelSettings.roomPlanLastNameFirst')" />
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-select v-model="hotel.defaultPolicyId"
                           :label="$t('admin.pageHotelSettings.defaultPolicyId')"
                           name="policyId"
                           cols="12">
            <option :value="null" :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"></option>
            <template v-for="policy in hotelSettings.policies.all(hotel.defaultPolicyId)">
              <option :value="policy.id" :key="policy.id">{{ policy.name }}</option>
            </template>
            <template slot="icon-inline-right" slot-scope="slot">
              <input-icon-button :border="true"
                                 :disabled="isEmpty(hotel.defaultPolicyId)"
                                 icon="edit"
                                 @click="$router.push({ name: 'policy', params: { policyId: hotel.defaultPolicyId }})"/>
            </template>
          </hm-input-select>
          <hm-input-multilang v-if="!hotel.onlineBookingHideBusinessTermsCheckbox"
                              v-model="hotel.translatableBusinessTermsUrl"
                              :lazy="true"
                              :label="$t('admin.pageHotelSettings.translatableBusinessTermsUrl')" />
          <hm-input-multilang v-model="hotel.translatableEnquiryUrl" :lazy="true" :label="$t('admin.pageHotelSettings.translatableEnquiryUrl')" />
        </hm-card-inner>
      </card>
      <card :title="$t('admin.pageHotelSettings.card.privacy')" cols="12 6@desktop">
        <hm-input :lazy="true" v-model="hotel.privacyDataController" :label="$t('admin.pageHotelSettings.privacyDataController')" :help="$t('admin.pageHotelSettings.privacyDataController_help')" />
        <hm-input :lazy="true" v-model="hotel.privacyOfficer" :label="$t('admin.pageHotelSettings.privacyOfficer')" :help="$t('admin.pageHotelSettings.privacyOfficer_help')" />
      </card>
      <card :title="$t('admin.pageHotelSettings.card.reservationColors')" cols="12 6@desktop" rows="2">
        <template v-for="i in 7">
          <hm-input-multilang :lazy="true"
                              :key="`category${i}`"
                              v-model="hotel.colorCategoryLabels['category' + i]"
                              :label="$t('admin.pageHotelSettings.colorCategoryLabels', { idx: i })"
                              :system-languages="true">
            <template slot="icon-inline-left">
              <div :class="[`hm-input__input__color--category-${i}`]" class="hm-input__input__color">&nbsp;</div>
            </template>
          </hm-input-multilang>
        </template>
        <template v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN') || !isEmpty(hotel.evaluateColorScript)">
          <hm-textarea :lazy="true"
                       :input-rows="isEmpty(hotel.evaluateColorScript) ? 5 : 30"
                       v-model="hotel.evaluateColorScript"
                       :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                       :label="`${$t('admin.pageHotelSettings.evaluateColorScript')} 🔒`"
                       :has-warning="!isEmpty(hotel.evaluateColorScript)"
                       class="hm-input__textarea--monospace"
                       spellcheck="off"
                       data-lt="false"
                       data-lt-active="false"
                       help="This function is DEPRECATED. We strongly advise NOT to use it, as it will be removed in a future version." />
        </template>
      </card>
      <card :title="`${$t('admin.pageHotelSettings.card.sysadminSettings')} 🔒`" cols="12 6@desktop">
        <hm-card-inner>
          <hm-col cols="12">
            <div style="position:relative; margin-bottom: 0.8rem;">{{ $t('admin.pageHotelSettings.hintSysadminSettings') }}</div>
          </hm-col>
        </hm-card-inner>
        <hm-card-inner>
          <hm-input-number :disabled="!hasRole('ROLE_SYSADMIN')"
                           v-model="hotel.maxNestedRelativeRateLevels"
                           :max-fraction-digits="0"
                           :min-fraction-digits="0"
                           :label="$t('admin.pageHotelSettings.maxNestedRelativeRateLevels')" />
          <!-- commented out for now - because it's disabled anyways -->
          <hm-input-toggle :disabled="!hasRole('ROLE_SYSADMIN')" v-model="hotel.postDiscountSeparately" :label="$t('admin.pageHotelSettings.postDiscountSeparately')" />
          <hm-input-toggle :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')" v-model="hotel.applyDiscountToManualOverrides" :label="$t('admin.pageHotelSettings.applyDiscountToManualOverrides')" />
          <hm-input-toggle :disabled="!hasRole('ROLE_SYSADMIN')" v-model="hotel.disableGuestProfileSmartMatching" :label="$t('admin.pageHotelSettings.disableGuestProfileSmartMatching')" :help="$t('admin.pageHotelSettings.disableGuestProfileSmartMatching_help')" />
          <hm-input-toggle v-if="hasRole('ROLE_SYSADMIN')" v-model="hotel.defaultChildRatePercentage" :label="$t('admin.pageHotelSettings.defaultChildRatePercentage')" />
          <hm-input-select v-if="hasRole('ROLE_SYSADMIN')" v-model="hotel.defaultRateMode" :label="$t('admin.pageHotelSettings.defaultRateMode')" enum-name="RateMode" />
          <!--          <hm-input-toggle v-if="hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')" v-model="hotel.google" label="Google?"/>-->
        </hm-card-inner>
      </card>
      <card v-if="hasRole('ROLE_SYSADMIN')" title="Disabled Fields 🔒" cols="12">
        <template v-for="(it, idx) in hotel.disabledFields">
          <hm-input :key="idx"
                    v-model="hotel.disabledFields[idx]"
                    :disabled="!hasRole('ROLE_HOTELMEISTER_SUPER_ADMIN')"
                    name="disabledField"
                    label="Disabled Field"
                    lazy />
        </template>
      </card>
    </template>
  </main>
</template>

<script>
/* eslint func-names: 0 */

import HotelmeisterMixin from '@/mixins/HotelmeisterMixin';
import { cloneDeep, groupBy, isEmpty, isNil, sortBy, sortedUniq } from 'lodash';
import SaveWatchMixin from '@/mixins/SaveWatchMixin';
import HmInputNumber from '../../lib/inputs/HmInputNumber';
import HmInputToggle from '../../lib/inputs/HmInputToggle';
import HmCardInner from '../../lib/layout/HmCardInner';
import HmInputMultilang from '../../lib/inputs/HmInputMultilang';
import HmInputTimeOfDaySlider from '../../lib/inputs/HmInputTimeOfDaySlider.vue';
import HmInputSelect from '../../lib/inputs/HmInputSelect.vue';
import AutoAddNewRowsMixin from '../../../mixins/AutoAddNewRowsMixin';
import HmTextarea from '../../lib/inputs/HmTextarea.vue';

export default {
  name: 'PageHotelSettings',
  components: { HmTextarea, HmInputSelect, HmInputTimeOfDaySlider, HmInputMultilang, HmCardInner, HmInputToggle, HmInputNumber },
  mixins: [HotelmeisterMixin, SaveWatchMixin, AutoAddNewRowsMixin],

  autosave: [
    {
      name: 'hotel',
      variable: 'hotel',
      event: 'hotelSettings.update',
      fn: function (cbSaveComplete) {
        this.axios.put(this.serviceUrls.hotelDetails.settings(this), this.hotel)
          .then((response) => {
            this.showMessages(response);
            cbSaveComplete();
          })
          .catch((error) => {
            this.showMessages(error.response);
            cbSaveComplete();
          });
      },
    },
  ],

  data() {
    return {
      hotel: null,
      onlineBookingSettings: {
        enabled: false,
      },
    };
  },

  computed: {
  },

  watch: {
    'hotel.disabledFields': {
      handler: function () {
        if (isNil(this.hotel.disabledFields)) {
          this.$set(this.hotel, 'disabledFields', []);
        }
        this.autoAddNewRows(
          this.hotel.disabledFields,
          null,
          it => isEmpty(it),
        );
      },
      deep: true,
    },
  },

  created: function () {
    this.loadData();
  },

  updated() {
  },

  beforeDestroy() {
  },

  methods: {
    loadData() {
      this.axios.get(this.serviceUrls.hotelDetails.settings(this))
        .then((response) => {
          const tmp = response.data.data;
          tmp.disabledFields = sortedUniq(tmp.disabledFields || []).filter(it => !isEmpty(it));
          this.hotel = tmp;
        });

      this.axios.get(this.serviceUrls.interfaces.onlineBooking.get(this, null))
        .then((response) => {
          this.onlineBookingSettings = response.data.data;
        });
    },
  },
};
</script>

<!-- Scoped SCSS  -->
<style lang="scss" scoped>
  @import '~styles/import';

</style>

<style lang="scss">

</style>



// WEBPACK FOOTER //
// src/components/pages/admin/PageHotelSettings.vue